import "./App.css";
import { ChakraProvider, extendTheme } from "@chakra-ui/react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";
import { Integrations } from "./views/app/Integrations";
import { History } from "./views/app/History";
import { Provider, useSelector } from "react-redux";
import { store, persistor, RootState } from "./store";
import { Loading } from "./components/shared/Loading";
import { PersistGate } from "reduxjs-toolkit-persist/integration/react";
import { MagicLinkLogin } from "./views/auth/MagicLinkLogin";
import { LoginRedirect } from "./views/auth/LoginRedirect";
import { Dashboard } from "./views/app/Dashboard";
import { Archive } from "./views/app/Archive";
import { Support } from "./views/app/Support";
import { colors } from "./constants/design.constants";

const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
  fonts: {
    heading: `"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif`,
    body: `"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif`,
    mono: `"IBM Plex Mono", ui-monospace, monospace`,
  },
  styles: {
    global: {
      // Keep body transparent — grid lives on #root (see index.css).
      // Chakra's default `bg` shorthand otherwise wipes background-image.
      "html, body": {
        bg: "transparent",
      },
      body: {
        color: colors.textPrimary,
      },
    },
  },
  colors: {
    brand: {
      50: colors.signalMuted,
      100: colors.void,
      200: colors.elevated,
      300: colors.panel,
      400: colors.hover,
      500: colors.signal,
      600: colors.signalDim,
      700: colors.brandMark,
    },
    gray: {
      50: "#F4F5F7",
      100: "#E8ECF2",
      200: "#C5CAD6",
      300: "#8B93A7",
      400: "#5C6578",
      500: "#3A4150",
      600: "#2A303C",
      700: "#1A1E26",
      800: "#14171C",
      900: "#0E1014",
    },
  },
  components: {
    Button: {
      baseStyle: {
        borderRadius: "2px",
        fontWeight: 500,
        letterSpacing: "0.02em",
      },
      variants: {
        solid: {
          bg: colors.signal,
          color: colors.void,
          _hover: { bg: "#7AC8DE" },
        },
        outline: {
          borderColor: colors.border,
          color: colors.textPrimary,
          _hover: { bg: colors.hover, borderColor: colors.signal },
        },
        ghost: {
          color: colors.textSecondary,
          _hover: { bg: colors.hover, color: colors.textPrimary },
        },
      },
    },
    Input: {
      defaultProps: { focusBorderColor: "brand.500" },
      variants: {
        outline: {
          field: {
            bg: colors.elevated,
            borderColor: colors.border,
            borderRadius: "2px",
            color: colors.textPrimary,
            _placeholder: { color: colors.textTertiary },
            _hover: { borderColor: colors.signalDim },
          },
        },
      },
    },
    Textarea: {
      defaultProps: { focusBorderColor: "brand.500" },
      variants: {
        outline: {
          bg: colors.elevated,
          borderColor: colors.border,
          borderRadius: "2px",
          color: colors.textPrimary,
          _placeholder: { color: colors.textTertiary },
        },
      },
    },
    Select: {
      defaultProps: { focusBorderColor: "brand.500" },
      variants: {
        outline: {
          field: {
            bg: colors.elevated,
            borderColor: colors.border,
            borderRadius: "2px",
            color: colors.textPrimary,
          },
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          bg: colors.elevated,
          border: `1px solid ${colors.border}`,
          borderRadius: "2px",
          color: colors.textPrimary,
        },
        overlay: {
          bg: "rgba(9, 10, 12, 0.75)",
          backdropFilter: "blur(4px)",
        },
      },
    },
    Table: {
      variants: {
        simple: {
          th: {
            borderColor: colors.border,
            color: colors.textTertiary,
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          },
          td: {
            borderColor: colors.borderSubtle,
            color: colors.textPrimary,
          },
        },
      },
    },
    Accordion: {
      baseStyle: {
        button: {
          _hover: { bg: colors.hover },
        },
      },
    },
    Tooltip: {
      baseStyle: {
        bg: colors.panel,
        color: colors.textPrimary,
        borderRadius: "2px",
        border: `1px solid ${colors.border}`,
        fontSize: "12px",
      },
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ChakraProvider theme={theme}>
        <PersistGate loading={<Loading loading={true} />} persistor={persistor}>
          <BrowserRouter>
            <Routes>
              <Route path="*" element={<Navigate to="/dashboard" />} />
              <Route
                path="/unauthorized"
                element={<Navigate to="/auth/app/login" />}
              />
              <Route path="/auth/app/login" element={<MagicLinkLogin />} />
              <Route
                path="/auth/login/redirect/:email/:password"
                element={<LoginRedirect />}
              />
              <Route path="/dashboard" element={<AuthenticatedRoute />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
              <Route path="/integrations" element={<AuthenticatedRoute />}>
                <Route path="/integrations" element={<Integrations />} />
              </Route>
              <Route path="/history/:id" element={<AuthenticatedRoute />}>
                <Route path="/history/:id" element={<History />} />
              </Route>
              <Route path="/archive" element={<AuthenticatedRoute />}>
                <Route path="/archive" element={<Archive />} />
              </Route>
              <Route path="/support" element={<AuthenticatedRoute />}>
                <Route path="/support" element={<Support />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </PersistGate>
      </ChakraProvider>
    </Provider>
  );
}

const AuthenticatedRoute = (props: { login?: boolean }) => {
  const token = useSelector((state: RootState) => state.token);

  const authenticated = props.login
    ? !!token.loginToken?.length
    : !!token.accessToken?.length;

  return authenticated ? <Outlet /> : <Navigate to="/auth/app/login" />;
};

export default App;
